// The client-side error channel. Extends the shared `AppError<C>` shape
// (@nightcrew/shared, packages/shared/src/result.ts:60) rather than inventing a
// parallel convention - every error the API can return already narrows to
// `{code, message}`; this file only adds the few *structured* refusal bodies the
// Slice-2 screens must render instead of a dead end (D33/D27/§2.4 of
// docs/design/specs/slice-2-onboarding.md).
//
// RECONCILED at Wave-3 integration against the real apps/api wire (the producer):
// `AppLimitRefusal` mirrors apps/api/src/apps/apps.schema.ts (HTTP 402, code
// "app_limit_reached", `plan` is a plain string because the server may report
// "unknown" for a missing plan row - entitlement fail-closed), and the route-card
// not-ready 400 carries code "intent_required" | "fingerprint_required"
// (apps/api/src/route/route.schema.ts RouteNotReady). The client still narrows
// defensively: an unrecognized error body renders the generic state, never a crash.
import { z } from "zod";
import type { AppError } from "@nightcrew/shared";

/** The structured app-limit refusal body (slice-2-onboarding.md §2.4 - never a dead end). */
export const AppLimitRefusal = z.object({
  code: z.literal("app_limit_reached"),
  message: z.string(),
  plan: z.string(),
  appLimit: z.number().int().positive(),
  appsUsed: z.number().int().nonnegative(),
  upgrade: z.object({
    available: z.boolean(),
    action: z.literal("ask_about_upgrading"),
  }),
});
export type AppLimitRefusal = z.infer<typeof AppLimitRefusal>;

/** The 400 typed error `GET /v1/apps/:id/route` returns before the card is ready (D33). */
export type RouteNotReadyError = AppError<"intent_required" | "fingerprint_required">;

/**
 * A generic boundary error body. Deliberately `AppError<string>`, not the closed
 * `ErrorCode` union (result.ts:41-53): the seam ports each carry their OWN closed error
 * code set (GitHostErrorCode, CloudErrorCode, DetectErrorCode - packages/shared/src/ports/*)
 * distinct from the general boundary's `ErrorCode`, so a single client-side narrowing
 * cannot assume one shared closed set. Call sites narrow on the literal `code` string
 * they expect from that specific endpoint.
 */
export type BoundaryError = AppError<string>;

/** Every failure shape `apiFetch` can resolve to. Consumers narrow on `kind`. */
export type ClientError =
  | { readonly kind: "network"; readonly message: string }
  | { readonly kind: "malformed_response"; readonly message: string }
  | { readonly kind: "app_limit_refusal"; readonly refusal: AppLimitRefusal }
  | { readonly kind: "route_not_ready"; readonly error: RouteNotReadyError }
  | { readonly kind: "boundary"; readonly error: BoundaryError };

/** True when a boundary error's code is exactly `code` - narrows `ClientError["boundary"]`. */
export const isBoundaryCode = (error: ClientError, code: string): boolean =>
  error.kind === "boundary" && error.error.code === code;
